// Common expense templates for Quick Add feature
export interface ExpenseTemplate {
  id: string;
  category: "streaming" | "housing" | "transportation" | "utilities" | "subscriptions" | "insurance" | "food";
  name: string;
  nameEs: string; // Spanish name
  price: number | null; // null = user must enter
  suggestedWave: 1 | 2;
  suggestedDueDay: number; // Day of month (1-31)
  isRecurring: boolean; // Always true for templates
  recurrenceFrequency: "monthly" | "biweekly" | "weekly";
  paymentType: "url" | "app" | "autopay";
  paymentUrl?: string;
  icon: string; // Emoji
  priority: number; // For sorting (most common first)
}

export const expenseTemplates: ExpenseTemplate[] = [
  // ========== STREAMING SERVICES (📺) ==========
  {
    id: "netflix",
    category: "streaming",
    name: "Netflix",
    nameEs: "Netflix",
    price: 15.49,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.netflix.com",
    icon: "📺",
    priority: 1,
  },
  {
    id: "spotify",
    category: "streaming",
    name: "Spotify",
    nameEs: "Spotify",
    price: 10.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.spotify.com",
    icon: "🎵",
    priority: 2,
  },
  {
    id: "hulu",
    category: "streaming",
    name: "Hulu",
    nameEs: "Hulu",
    price: 7.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.hulu.com",
    icon: "📺",
    priority: 3,
  },
  {
    id: "disney-plus",
    category: "streaming",
    name: "Disney+",
    nameEs: "Disney+",
    price: 7.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.disneyplus.com",
    icon: "🏰",
    priority: 4,
  },
  {
    id: "youtube-premium",
    category: "streaming",
    name: "YouTube Premium",
    nameEs: "YouTube Premium",
    price: 13.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.youtube.com/premium",
    icon: "▶️",
    priority: 5,
  },
  {
    id: "amazon-prime",
    category: "streaming",
    name: "Amazon Prime",
    nameEs: "Amazon Prime",
    price: 14.99,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.amazon.com/prime",
    icon: "📦",
    priority: 6,
  },

  // ========== HOUSING (🏠) ==========
  {
    id: "rent",
    category: "housing",
    name: "Rent",
    nameEs: "Renta",
    price: null, // User must enter
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🏠",
    priority: 1,
  },
  {
    id: "mortgage",
    category: "housing",
    name: "Mortgage",
    nameEs: "Hipoteca",
    price: null,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🏡",
    priority: 2,
  },
  {
    id: "renters-insurance",
    category: "housing",
    name: "Renters Insurance",
    nameEs: "Seguro de Inquilino",
    price: 15,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🛡️",
    priority: 3,
  },
  {
    id: "hoa-fees",
    category: "housing",
    name: "HOA Fees",
    nameEs: "Cuotas de HOA",
    price: null,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🏘️",
    priority: 4,
  },

  // ========== UTILITIES (💡) ==========
  {
    id: "electric",
    category: "utilities",
    name: "Electric Bill",
    nameEs: "Factura de Electricidad",
    price: null,
    suggestedWave: 2,
    suggestedDueDay: 10,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "💡",
    priority: 1,
  },
  {
    id: "water",
    category: "utilities",
    name: "Water Bill",
    nameEs: "Factura de Agua",
    price: null,
    suggestedWave: 2,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "💧",
    priority: 2,
  },
  {
    id: "gas",
    category: "utilities",
    name: "Gas Bill",
    nameEs: "Factura de Gas",
    price: null,
    suggestedWave: 2,
    suggestedDueDay: 10,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🔥",
    priority: 3,
  },
  {
    id: "internet",
    category: "utilities",
    name: "Internet",
    nameEs: "Internet",
    price: 60,
    suggestedWave: 1,
    suggestedDueDay: 5,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🌐",
    priority: 4,
  },
  {
    id: "phone",
    category: "utilities",
    name: "Phone Bill",
    nameEs: "Factura de Teléfono",
    price: 50,
    suggestedWave: 1,
    suggestedDueDay: 10,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "📱",
    priority: 5,
  },

  // ========== TRANSPORTATION (🚗) ==========
  {
    id: "car-payment",
    category: "transportation",
    name: "Car Payment",
    nameEs: "Pago de Auto",
    price: null,
    suggestedWave: 1,
    suggestedDueDay: 5,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🚗",
    priority: 1,
  },
  {
    id: "car-insurance",
    category: "transportation",
    name: "Car Insurance",
    nameEs: "Seguro de Auto",
    price: 150,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🛡️",
    priority: 2,
  },
  {
    id: "gas-fuel",
    category: "transportation",
    name: "Gas/Fuel",
    nameEs: "Gasolina",
    price: 200,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "⛽",
    priority: 3,
  },

  // ========== SUBSCRIPTIONS (📱) ==========
  {
    id: "gym",
    category: "subscriptions",
    name: "Gym Membership",
    nameEs: "Membresía de Gimnasio",
    price: 30,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "💪",
    priority: 1,
  },
  {
    id: "icloud",
    category: "subscriptions",
    name: "iCloud Storage",
    nameEs: "Almacenamiento iCloud",
    price: 0.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "☁️",
    priority: 2,
  },
  {
    id: "adobe",
    category: "subscriptions",
    name: "Adobe Creative Cloud",
    nameEs: "Adobe Creative Cloud",
    price: 54.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    paymentUrl: "www.adobe.com",
    icon: "🎨",
    priority: 3,
  },
  {
    id: "google-one",
    category: "subscriptions",
    name: "Google One",
    nameEs: "Google One",
    price: 1.99,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "☁️",
    priority: 4,
  },

  // ========== INSURANCE (🛡️) ==========
  {
    id: "health-insurance",
    category: "insurance",
    name: "Health Insurance",
    nameEs: "Seguro Médico",
    price: null,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🏥",
    priority: 1,
  },
  {
    id: "life-insurance",
    category: "insurance",
    name: "Life Insurance",
    nameEs: "Seguro de Vida",
    price: null,
    suggestedWave: 1,
    suggestedDueDay: 1,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🛡️",
    priority: 2,
  },

  // ========== FOOD (🍔) ==========
  {
    id: "groceries",
    category: "food",
    name: "Groceries",
    nameEs: "Compras de Comestibles",
    price: 400,
    suggestedWave: 1,
    suggestedDueDay: 15,
    isRecurring: true,
    recurrenceFrequency: "monthly",
    paymentType: "url",
    icon: "🛒",
    priority: 1,
  },
];

// Helper function to get templates by category
export const getTemplatesByCategory = (category: string) => {
  return expenseTemplates.filter((t) => t.category === category);
};

// Helper function to get all categories
export const getTemplateCategories = () => {
  const categories = Array.from(new Set(expenseTemplates.map((t) => t.category)));
  return categories;
};
